<template>
  <div class="payment-link">
    <b-navbar class="payment-link-navbar d-none d-md-flex">
      <b-navbar-brand href="#">
        <amg-icon
          v-if="!isValidLink"
          icon="AmgIcon"
          size="40"
          class="pointer invert"
        />
        <div
          v-if="
            isPayment &&
            form.payment_option === 'INITIAL PAYMENT' &&
            isValidLink &&
            !isLoading
          "
          class="d-flex align-items-center justify-content-start"
        >
          <b-img
            v-for="(detail, index) in form.payment_link_details"
            :key="index"
            class="client-program mr-1"
            :src="getProgramImageRoute(detail.program_id)"
            v-b-tooltip.hover.top
            :title="detail.program"
          />
        </div>

        <div
          v-if="
            form.payment_option !== 'INITIAL PAYMENT' &&
            isValidLink &&
            !isLoading
          "
          class="d-flex align-items-center justify-content-start"
        >
          <b-img
            class="client-program mr-1"
            :src="getProgramImageRoute(form.first_program_id)"
            v-b-tooltip.hover.top
            :title="form.program"
          />
        </div>
      </b-navbar-brand>

      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav class="ml-auto d-flex align-items-center">
          <b-nav-item href="#" class="mr-2">
            <b-form-select
              size="sm"
              v-model="selectedLanguage"
              :options="languageOptions"
              class="language-select"
            ></b-form-select>
          </b-nav-item>

          <b-nav-item href="#" class="mr-2">
            <dark-toggler class="invert" :size="'20'" />
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <b-container v-if="isLoading === true">
      <SecurePaymentFormSkeleton />
    </b-container>

    <template v-if="isLoading === false">
      <b-container v-if="isValidLink && !successResponse">
        <b-card :class="getPaymentLinkClass()">
          <b-row class="mb-05">
            <b-col
              v-if="!isPayment"
              cols="3"
              class="d-flex justify-content-between align-items-center"
            >
              <div class="d-flex align-items-center justify-content-start">
                <b-img
                  class="client-program mr-1"
                  :src="getProgramImageRoute(form.first_program_id)"
                  v-b-tooltip.hover.top
                  :title="form.program"
                />
              </div>
            </b-col>

            <b-col
              :cols="isPayment ? 12 : 9"
              class="d-flex mt-1 mt-sm-0 align-items-center justify-content-end"
            >
              <div class="border border-secondary rounded-lg px-05">
                <span class="text-no-fw"> {{ t.client }} :</span>
                <span class="text"> {{ form.name }} </span>
              </div>
            </b-col>
          </b-row>

          <b-row>
            <b-col
              :cols="12"
              class="d-flex justify-content-start mt-sm-0 justify-content-sm-center align-items-center"
            >
              <span class="type">
                {{ isRegisterCard ? t.registerCard : t.registerPayment }}
              </span>
            </b-col>

            <b-col
              :cols="12"
              class="border-bottom pb-1"
              :class="isRegisterCard ? 'mb-1' : ''"
            >
              <div
                class="d-flex justify-content-start mt-1 mt-sm-0 justify-content-sm-center align-items-center"
              >
                <feather-icon
                  class="mr-05 text-gray"
                  icon="LockIcon"
                  size="16"
                />
                <span class="encrypted-message">
                  {{ t.encryptedMessage }}
                </span>
              </div>
            </b-col>
          </b-row>

          <validation-observer ref="form">
            <b-row>
              <!-- Formulario nuevo de pago -->
              <div v-if="isMobile && isPayment" class="transaction-list-mobile">
                <div
                  v-for="(transaction, index) in form.payment_link_details"
                  :key="index"
                  class="transaction-card py-1 px-1 mb-1 rounded-lg"
                  :class="index === 0 ? 'mt-1' : ''"
                >
                  <div class="transaction-info">
                    <div
                      class="d-flex justify-content-start transaction-value align-items-center"
                    >
                      <b-img
                        class="client-program mr-05"
                        :src="getProgramImageRoute(transaction.program_id)"
                        v-b-tooltip.hover.top
                        :title="transaction.program"
                      />

                      <div class="d-flex flex-column ml-1">
                        <span class="text">
                          <span
                            class="text-big"
                            v-if="form.payment_option === 'INITIAL PAYMENT'"
                          >
                            {{ t.initialPayment }} <br />
                          </span>
                          <span
                            class="text-big"
                            v-if="form.payment_option === 'MONTHLY PAYMENT'"
                          >
                            {{ t.monthlyPayment }} <br />
                          </span>
                           <span
                            class="text-big"
                            v-if="form.payment_option === 'CHARGE'"
                          >
                            {{ t.monthlyPayment }} <br />
                          </span>
                          <span class="text-big">
                            {{ transaction.program }}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div class="text text-right text-big">
                      {{ formatCurrency(transaction.amount) }}
                    </div>
                  </div>

                  <div
                    v-if="transaction.is_automatic_payment"
                    class="transaction-info custom-border-top mt-05 pt-05"
                  >
                    <span class="text text-big">
                      {{ t.configureAutomaticPayment }}
                    </span>
                  </div>

                  <div
                    v-if="transaction.is_automatic_payment"
                    class="transaction-info pt-05"
                  >
                    <span class="text">
                      {{ t.automaticPaymentAmount }}
                    </span>

                    <span class="text">
                      {{ formatCurrency(transaction.automatic_payment_amount) }}
                    </span>
                  </div>

                  <div
                    v-if="transaction.is_automatic_payment"
                    class="transaction-info pt-05"
                  >
                    <span class="text">
                      {{ t.automaticPaymentDay }}
                    </span>

                    <ValidationProvider
                      :name="`automaticPaymentDate-${index}`"
                      :rules="getRules('automaticPaymentDate')"
                      v-slot="{ errors, failedRules }"
                    >
                      <div
                        class="d-flex align-items-center justify-content-center"
                      >
                        <b-form-select
                          v-model="transaction.automatic_payment_date_number"
                          size="sm"
                          @change="onChangeAutomaticPaymentDate($event, index)"
                          :state="
                            getState(
                              errors,
                              transaction.automatic_payment_date_number
                            )
                          "
                          :class="errors.length > 0 ? 'is-invalid' : ''"
                        >
                          <b-form-select-option
                            v-for="date in dates"
                            :key="date.value"
                            :value="date.value"
                          >
                            {{ date.label }}
                          </b-form-select-option>
                        </b-form-select>
                      </div>

                      <b-form-invalid-feedback
                        v-if="errors.length > 0"
                        style="text-transform: lowercase; font-weight: 500"
                      >
                        {{ getError(failedRules, "automaticPaymentDate") }}
                      </b-form-invalid-feedback>
                    </ValidationProvider>
                  </div>

                  <div
                    v-if="
                      transaction.is_automatic_payment &&
                      transaction.automatic_payment_date_text
                    "
                    class="transaction-info pt-05"
                  >
                    <span class="text">
                      {{ t.scheduledPaymentDate }}
                    </span>

                    <span class="text">
                      {{ transaction.automatic_payment_date_text }}
                    </span>
                  </div>
                </div>

                <!-- Total -->
                <div
                  v-if="
                    form.payment_option === 'INITIAL PAYMENT' &&
                    form.payment_link_details.length > 1
                  "
                  class="transaction-card py-1 px-1 mb-1 rounded-lg"
                >
                  <div class="transaction-info">
                    <div class="text-left">
                      <span class="text">{{ t.totalAmount }} </span>
                    </div>

                    <div class="text text-right">
                      {{ formatCurrency(form.amount) }}
                    </div>
                  </div>
                </div>
              </div>

              <div
                v-if="!isMobile && isPayment"
                class="transaction-list-desktop"
              >
                <div
                  v-for="(transaction, index) in form.payment_link_details"
                  :key="'transaction-' + index"
                  class="transaction-row"
                >
                  <div
                    class="d-flex justify-content-start transaction-value align-items-center"
                  >
                    <b-img
                      class="client-program mr-05"
                      :src="getProgramImageRoute(transaction.program_id)"
                      v-b-tooltip.hover.top
                      :title="transaction.program"
                    />

                    <div class="d-flex flex-column ml-1">
                      <span
                        class="text-big"
                        v-if="form.payment_option === 'INITIAL PAYMENT'"
                      >
                        {{ t.initialPayment }} <br />
                      </span>
                      <span
                        class="text-big"
                        v-if="form.payment_option === 'MONTHLY PAYMENT'"
                      >
                        {{ t.monthlyPayment }} <br />
                      </span>
                      <span
                        class="text-big"
                        v-if="form.payment_option === 'CHARGE'"
                      >
                        {{ form.concept }} <br />
                      </span>
                      <!-- concept here -->
                      <span class="text-big">{{ transaction.program }}</span>
                    </div>
                  </div>

                  <div
                    class="d-flex align-items-center transaction-value font-weight-bold"
                  >
                    <span class="text-big">
                      {{ formatCurrency(transaction.amount) }}</span
                    >
                  </div>

                  <div
                    v-if="transaction.is_automatic_payment"
                    class="transaction-detail"
                    style="
                      border-top-left-radius: 8px;
                      border-bottom-left-radius: 8px;
                    "
                  >
                    <div class="d-flex align-items-end transaction-value pl-0">
                      <div
                        class="d-flex align-items-center mt-05 font-weight-bold text-big"
                      >
                        {{ t.configureAutomaticPayment }}
                      </div>
                    </div>

                    <div>
                      <span>{{ t.automaticPaymentAmount }}:</span>
                      <span class="font-weight-bold">
                        {{
                          formatCurrency(transaction.automatic_payment_amount)
                        }}
                      </span>
                    </div>
                  </div>

                  <div
                    v-if="transaction.is_automatic_payment"
                    class="transaction-detail"
                    style="
                      border-top-right-radius: 8px;
                      border-bottom-right-radius: 8px;
                    "
                  >
                    <div class="d-flex align-items-end transaction-value pl-0">
                      <ValidationProvider
                        :name="`automaticPaymentDate-${index}`"
                        :rules="getRules('automaticPaymentDate')"
                        v-slot="{ errors, failedRules }"
                      >
                        <div
                          class="d-flex align-items-center justify-content-center"
                        >
                          <span class="mr-1">{{ t.automaticPaymentDay }}:</span>
                          <b-form-select
                            v-model="transaction.automatic_payment_date_number"
                            size="sm"
                            @change="
                              onChangeAutomaticPaymentDate($event, index)
                            "
                            class="d-inline-block"
                            style="max-width: max-content !important"
                            :state="
                              getState(
                                errors,
                                transaction.automatic_payment_date_number
                              )
                            "
                            :class="errors.length > 0 ? 'is-invalid' : ''"
                          >
                            <b-form-select-option
                              v-for="date in dates"
                              :key="date.value"
                              :value="date.value"
                            >
                              {{ date.label }}
                            </b-form-select-option>
                          </b-form-select>
                        </div>

                        <b-form-invalid-feedback
                          v-if="errors.length > 0"
                          style="text-transform: lowercase; font-weight: 500"
                        >
                          {{ getError(failedRules, "automaticPaymentDate") }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </div>

                    <div>
                      <span>{{ t.scheduledPaymentDate }}:</span>
                      <span
                        class="font-weight-bold"
                        v-if="transaction.automatic_payment_date_text"
                      >
                        {{ transaction.automatic_payment_date_text }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Fin de nuevo formulario de pago -->

              <!-- Formulario de tarjeta -->
              <b-col
                class="p-0 px-1"
                cols="12"
                :class="isPayment ? 'mt-05' : ''"
              >
                <ValidationProvider
                  name="cardholder"
                  :rules="getRules('cardholder')"
                  v-slot="{ errors, failedRules }"
                >
                  <b-form-group label-for="card-holder">
                    <template #label>
                      <span class="custom-label-class">{{ t.cardholder }}</span>
                    </template>

                    <b-form-input
                      class="input-uppercase"
                      id="card-holder"
                      v-model="payment.cardholder"
                      type="text"
                      required
                      :placeholder="t.placeholder.cardholder"
                      :state="getState(errors, payment.cardholder)"
                      :class="errors.length > 0 ? 'is-invalid' : ''"
                    ></b-form-input>
                    <b-form-invalid-feedback v-if="errors.length > 0">
                      {{ getError(failedRules, "cardholder") }}
                    </b-form-invalid-feedback>
                  </b-form-group>
                </ValidationProvider>

                <b-form-group label-for="card-number">
                  <template #label>
                    <span class="custom-label-class">{{ t.cardNumber }}</span>
                  </template>

                  <ValidationProvider
                    name="cardNumber"
                    :rules="getRules('cardNumber')"
                    v-slot="{ errors, failedRules }"
                  >
                    <div class="position-relative">
                      <!-- Imagen de la tarjeta -->
                      <img
                        v-if="cardImage"
                        :src="require(`@/assets/images/cards/${cardImage}`)"
                        :alt="t.alt.cardImage"
                        class="card-icon-left"
                        :style="errors.length > 0 ? 'top: 34%' : 'top: 50%'"
                      />
                      <feather-icon
                        v-else
                        class="card-icon-left-feather"
                        icon="CreditCardIcon"
                        :style="errors.length > 0 ? 'top: 34%' : 'top: 50%'"
                      />

                      <!-- Input con padding izquierdo ajustado -->
                      <b-form-input
                        id="card-number"
                        v-model="payment.cardNumber"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        required
                        class="card-number-input pl-10"
                        :placeholder="t.placeholder.cardNumber"
                        @input="formatCardNumber"
                        @keypress="onlyAllowNumbers($event)"
                        :state="getState(errors, payment.cardNumber)"
                        :class="errors.length > 0 ? 'is-invalid' : ''"
                        maxlength="19"
                      ></b-form-input>

                      <b-form-invalid-feedback v-if="errors.length > 0">
                        {{ getError(failedRules, "cardNumber") }}
                      </b-form-invalid-feedback>
                    </div>
                  </ValidationProvider>
                </b-form-group>

                <b-form-row>
                  <b-col xs="8">
                    <b-form-group label-for="expiry-date">
                      <template #label>
                        <span class="custom-label-class">{{
                          t.expiryDate
                        }}</span>

                        <b-tooltip
                          target="expiry-date"
                          placement="top"
                          :custom-class="'pl-tooltip'"
                        >
                          <div
                            style="
                              font-size: 0.8rem;
                              display: flex;
                              align-items: center;
                              white-space: nowrap;
                            "
                            class="d-flex justify-content-between align-items-center"
                          >
                            <span
                              style="text-align: left !important; flex-grow: 1"
                              v-html="t.tooltip.expiryDate"
                            ></span>

                            <b-img
                              style="
                                margin-left: 0.5rem;
                                width: 80px;
                                object-fit: contain;
                              "
                              src="~@/assets/images/icons/expiry-date-tooltip.svg"
                              alt="Expiry Date Tooltip"
                              fluid
                            />
                          </div>
                        </b-tooltip>

                        <feather-icon
                          id="expiry-date"
                          class="ml-05 cursor-pointer"
                          icon="HelpCircleIcon"
                          size="15"
                        />
                      </template>

                      <ValidationProvider
                        name="expiryDate"
                        :rules="getRules('expiryDate')"
                        v-slot="{ errors, failedRules }"
                      >
                        <b-form-input
                          id="expiry-date"
                          v-model="formattedExpiryDate"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          type="text"
                          required
                          placeholder="MM/YY"
                          maxlength="5"
                          @input="formatExpiryDate"
                          @keypress="onlyAllowNumbers($event)"
                          :state="getState(errors, formattedExpiryDate)"
                          :class="errors.length > 0 ? 'is-invalid' : ''"
                        ></b-form-input>
                        <b-form-invalid-feedback v-if="errors.length > 0">
                          {{ getError(failedRules, "expiryDate") }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-form-group>
                  </b-col>

                  <b-col xs="4">
                    <b-form-group label-for="cvv">
                      <template #label>
                        <span class="custom-label-class">{{ t.cvv }}</span>

                        <b-tooltip
                          target="cvv"
                          placement="top"
                          :custom-class="'pl-tooltip'"
                        >
                          <div
                            style="
                              font-size: 0.8rem;
                              display: flex;
                              align-items: center;
                              white-space: nowrap;
                            "
                            class="d-flex justify-content-between align-items-center"
                          >
                            <span
                              style="text-align: left !important; flex-grow: 1"
                              v-html="t.tooltip.cvv"
                            ></span>

                            <b-img
                              style="
                                margin-left: 0.5rem;
                                width: 80px;
                                object-fit: contain;
                              "
                              src="~@/assets/images/icons/cvv.svg"
                              alt="CVV Tooltip"
                              fluid
                            />
                          </div>
                        </b-tooltip>

                        <feather-icon
                          id="cvv"
                          class="ml-05 cursor-pointer"
                          icon="HelpCircleIcon"
                          size="15"
                        />
                      </template>

                      <ValidationProvider
                        name="cvv"
                        :rules="getRules('cvv')"
                        v-slot="{ errors, failedRules }"
                        tag="div"
                      >
                        <b-form-input
                          id="cvv"
                          v-model="payment.cvv"
                          inputmode="numeric"
                          pattern="[0-9]*"
                          type="password"
                          required
                          :placeholder="t.placeholder.cvv"
                          maxlength="4"
                          :state="getState(errors, payment.cvv)"
                          :class="errors.length > 0 ? 'is-invalid' : ''"
                        ></b-form-input>
                        <b-form-invalid-feedback v-if="errors.length > 0">
                          {{ getError(failedRules, "cvv") }}
                        </b-form-invalid-feedback>
                      </ValidationProvider>
                    </b-form-group>
                  </b-col>
                </b-form-row>

                <b-button
                  class="payment-button"
                  type="button"
                  variant="success"
                  block
                  @click="handleOnSubmit"
                  :disabled="isProcessing"
                >
                  <b-spinner
                    v-if="isProcessing"
                    variant="primary"
                    label="Spinning"
                  ></b-spinner>

                  <span v-if="isRegisterCard && !isProcessing">
                    {{ t.button.save }}
                  </span>
                  <span v-if="isPayment && !isProcessing">
                    {{ t.button.pay }}
                    {{ formatCurrency(form.amount) }}
                  </span>
                </b-button>
              </b-col>
            </b-row>
          </validation-observer>
        </b-card>
      </b-container>

      <SecurePaymentInvalid
        v-if="!isValidLink && !successResponse"
        :selectedLanguage="selectedLanguage"
      />

      <SecurePaymentValid
        v-if="successResponse"
        :selectedLanguage="selectedLanguage"
        :messages="messages"
        :paymmentLinks="form.payment_link_details"
      />
    </template>
  </div>
</template>

<script>
import DarkToggler from "@core/layouts/components/app-navbar/components/DarkToggler.vue";
import translations from "@/views/pages/secure-payment-form/data/translations.js";
import SecurePaymentService from "@/views/pages/secure-payment-form/service/secure.payment.service.js";
import { encryptTextWithRsa, encryptData } from "@/utils/encryption.js";
import { securePaymentRules } from "@/views/pages/secure-payment-form/data/secure.payment.rules.js";
import SecurePaymentInvalid from "@/views/pages/secure-payment-form/SecurePaymentInvalid.vue";
import SecurePaymentValid from "@/views/pages/secure-payment-form/SecurePaymentValid.vue";
import SecurePaymentFormSkeleton from "@/views/pages/secure-payment-form/SecurePaymentFormSkeleton.vue";

export default {
  components: {
    DarkToggler,
    SecurePaymentInvalid,
    SecurePaymentValid,
    SecurePaymentFormSkeleton,
  },
  data() {
    return {
      messages: [],
      form: {
        concept: null,
        name: null,
        first_name: null,
        last_name: null,
        amount: null,
        program_id: null,
        type: null,
        currency: "USD", // set default value
        language: null,
        lead_id: null, // nullable
        first_program_id: null,
        payment_link_details: null,
        payment_option: null,
        link_type: null,
      },
      payment: {
        cardNumber: null,
        expiryMonth: null,
        expiryYear: null,
        cvv: null,
        cardholder: null,
      },
      cardImage: null,
      languageOptions: [
        { value: "es", text: "Español" },
        { value: "en", text: "English" },
      ],
      selectedLanguage: "en",
      securePaymentRules, // reglas por campo
      formattedExpiryDate: "", // Campo que mostrará MM/YY
      isLoading: null,
      isValidLink: false,
      successResponse: false, // Indica si se ha completado la operación con éxito
      isProcessing: false,
      dates: [
        {
          label: "5",
          value: 5,
        },
        {
          label: "10",
          value: 10,
        },
        {
          label: "15",
          value: 15,
        },
        {
          label: "20",
          value: 20,
        },
        {
          label: "25",
          value: 25,
        },
        {
          label: "30",
          value: 30,
        },
      ],
      isMobile: window.innerWidth <= 768,
    };
  },
  computed: {
    t() {
      return translations[this.selectedLanguage];
    },
    uniqueCode() {
      return this.$route.params.unique_code;
    },
    isPayment() {
      return (
        this.form.type === "REGISTER AND PAY" ||
        this.form.type === "REGISTER AND PAY AND SCHEDULE RB"
      );
    },
    isRegisterCard() {
      return this.form.type === "REGISTER CARD";
    },
    hasAutomaticPayment() {
      return (
        (this.form.type === "REGISTER AND PAY" ||
          this.form.type === "REGISTER AND PAY AND SCHEDULE RB") &&
        (this.form.payment_option === "INITIAL PAYMENT" ||
          this.form.payment_option === "MONTHLY PAYMENT") &&
        Array.isArray(this.form.payment_link_details) &&
        this.form.payment_link_details.some(
          (detail) => detail.is_automatic_payment
        )
      );
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 768;
    },
    getPaymentLinkClass() {
      if (!this.isPayment) return "payment-link-card";
      return this.hasAutomaticPayment
        ? "payment-link-pay-automatic"
        : "payment-link-pay";
    },
    onChangeAutomaticPaymentDate(value, index) {
      const today = new Date();
      const currentMonth = today.getMonth() + 1; // Los meses en JS son base 0
      const currentYear = today.getFullYear();

      let paymentDay = value;
      let paymentMonth = currentMonth + 1;
      let paymentYear = currentYear;

      // Si el próximo mes es enero (cambio de año)
      if (paymentMonth > 12) {
        paymentMonth = 1;
        paymentYear += 1;
      }

      // Verificar la cantidad de días en el próximo mes calculado
      const daysInMonth = new Date(paymentYear, paymentMonth, 0).getDate();
      if (paymentDay > daysInMonth) {
        paymentDay = daysInMonth;
      }

      // Formatear día y mes con ceros a la izquierda si es necesario
      const formattedDay = String(paymentDay).padStart(2, "0");
      const formattedMonth = String(paymentMonth).padStart(2, "0");

      // Formatos de fecha
      const paymentDateUS = `${formattedMonth}/${formattedDay}/${paymentYear}`; // 04/05/2025
      const paymentDateISO = `${paymentYear}/${formattedMonth}/${formattedDay}`; // 2025/04/05

      // Asegurar la reactividad usando Vue.set
      const updatedTransaction = {
        ...this.form.payment_link_details[index],
        automatic_payment_date_text: paymentDateUS,
        automatic_payment_date: paymentDateISO,
        automatic_payment_date_number: value, // Actualiza también el número seleccionado
      };

      // Actualizar el objeto en el array
      this.$set(this.form.payment_link_details, index, updatedTransaction);
    },
    setFavIcon() {
      if (this.isPayment && this.form.payment_option === "INITIAL PAYMENT") {
        const lastImage =
          this.form.payment_link_details[
            this.form.payment_link_details.length - 1
          ];

        this.changeFavicon(this.getProgramImageRoute(lastImage.program_id));
      }

      if (this.form.payment_option !== "INITIAL PAYMENT") {
        this.changeFavicon(
          this.getProgramImageRoute(this.form.first_program_id)
        );
      }
    },
    changeFavicon(iconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement("link");
        link.rel = "icon";
        document.head.appendChild(link);
      }
      link.href = iconUrl;
    },
    formatExpiryDate(event) {
      // Obtén el valor formateado desde el input y elimina cualquier carácter no numérico
      let value = this.formattedExpiryDate.replace(/[^0-9]/g, ""); // Elimina caracteres no numéricos

      // Si la longitud del valor es mayor a 2, agregar el separador "/"
      if (value.length > 2) {
        value = value.slice(0, 2) + "/" + value.slice(2, 4); // Formatea a MM/YY
      }

      // Asigna el valor formateado a `formattedExpiryDate`
      this.formattedExpiryDate = value;

      // Sincronizar mes y año con `payment`
      const [month, year] = value.split("/");
      this.payment.expiryMonth = month || "";
      this.payment.expiryYear = year || "";
    },
    onlyAllowNumbers(event) {
      const key = event.key;
      // Permite sólo números y las teclas de control como backspace
      if (!/[0-9]/.test(key) && key !== "Backspace" && key !== "Delete") {
        event.preventDefault(); // Previene que el carácter no numérico sea ingresado
      }
    },
    formatCardNumber() {
      // Elimina todos los caracteres no numéricos
      let formatted = this.payment.cardNumber.replace(/\D/g, "");

      // Inserta los separadores "-" cada 4 dígitos
      formatted = formatted.replace(/(.{4})(?=.)/g, "$1-");

      // Asigna el valor formateado de vuelta al modelo
      this.payment.cardNumber = formatted;

      this.onCardNumberInput();
    },
    getRules(field) {
      return this.securePaymentRules[field];
    },
    getState(errorsList, field) {
      return errorsList.length > 0 ? false : field ? true : null;
    },
    getError(error, validationProperty) {
      let property = Object.keys(error)[0];

      const messages = {
        required: `${this.t.validation.required}`,
        "only-letters": `${this.t.validation.onlyLetters}`,
        min: `${this.t.validation.mustBeAtLeast} ${this.securePaymentRules[validationProperty].min} ${this.t.validation.charactersLong}`,
        max: `${this.t.validation.mustNotExceed} ${this.securePaymentRules[validationProperty].max} ${this.t.validation.characters}.`,
        is_numeric: `${this.t.validation.mustBeNumeric}`,
        "valid-expiration-date": `${this.t.validation.invalidExpirationDate}`,
      };

      return messages[property];
    },
    formatCurrency(amount, symbolPosition = "right") {
      const formatter = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: this.form.currency,
      });

      const formattedAmount = formatter.format(amount);

      // Detectar la posición del símbolo de moneda en el valor formateado
      const isSymbolFirst = formattedAmount.startsWith(
        formatter.format(0).replace(/\d/g, "")
      );

      if (symbolPosition === "left" && !isSymbolFirst) {
        // Mover el símbolo a la izquierda si no está al principio
        const [value, symbol] = formattedAmount.match(/([^0-9.,]+)|([\d.,]+)/g);
        return `${symbol} ${value}`;
      }

      if (symbolPosition === "right" && isSymbolFirst) {
        // Mover el símbolo a la derecha si está al principio
        const [symbol, value] = formattedAmount.match(/([^0-9.,]+)|([\d.,]+)/g);
        return `${value} ${symbol}`;
      }

      // Devolver el valor formateado directamente si ya está en la posición correcta
      return formattedAmount;
    },
    onCardNumberInput() {
      // Elimina cualquier carácter no numérico (incluye espacios, guiones, etc.)
      const cardNumber = this.payment.cardNumber.replace(/\D/g, "");

      // Tipos de tarjetas con sus prefijos (inclusión de más tarjetas como Diners Club, etc.)
      const cardTypes = {
        visa: { prefix: ["4"], image: "visa2.png" },
        mastercard: {
          prefix: [
            "5",
            "222",
            "23",
            "24",
            "25",
            "26",
            "27",
            "28",
            "29",
            "30",
            "31",
            "32",
            "33",
          ],
          image: "mastercard2.png",
        },
        amex: { prefix: ["34", "37"], image: "american.png" },
        discover: {
          prefix: ["6011", "622", "64", "65"],
          image: "Discover.jpg",
        },
        jcb: { prefix: ["35"], image: "jcb2.png" },
        diners: { prefix: ["30", "36", "38", "39"], image: "diners.png" },
      };

      // Itera sobre cada tipo de tarjeta y verifica si el número coincide con algún prefijo
      for (const type in cardTypes) {
        if (
          cardTypes[type].prefix.some((prefix) => cardNumber.startsWith(prefix))
        ) {
          this.cardImage = cardTypes[type].image;
          return;
        }
      }

      // Si no se encuentra ninguna coincidencia, limpia la imagen de la tarjeta
      this.cardImage = null;
    },
    async handleOnSubmit() {
      try {
        const validate = await this.$refs.form.validate();

        if (!validate) return;

        this.isProcessing = true;

        let sensitiveInfo = {
          cardholder: this.payment.cardholder.toUpperCase(),
          cardNumber: this.payment.cardNumber,
          expiryMonth: this.payment.expiryMonth,
          expiryYear: this.payment.expiryYear,
          cvv: this.payment.cvv,
          unique_code: this.uniqueCode,
          lead_id: this.form.lead_id,
          type: this.form.type,
          payment_link_details_front: this.form.payment_link_details,
        };

        // Convertir objeto a JSON y luego a ArrayBuffer
        const encoder = new TextEncoder();
        const plainData = encoder.encode(JSON.stringify(sensitiveInfo));
        const encryptedData = await encryptData(plainData);

        let payload = {
          encryptedPayload: encryptedData.encryptedData, // Datos cifrados (sensible)
          iv: encryptedData.iv, // IV en Base64 (no es sensible)
          tag: encryptedData.tag, // Tag en Base64 (no es sensible)
          key: encryptTextWithRsa(encryptedData.key), // Clave de encriptación cifrada con RSA (sensible si no se cifra)
        };
        const { data } = await SecurePaymentService.register(payload);

        // const title = this.t.successMessages.success;
        // const text =
        //   this.t.successMessages?.[data.messageKey] || "Translation not found";
        this.successResponse = true;

        if (this.form.payment_option === "INITIAL PAYMENT") {
          this.messages = data;
        }
        // this.showSuccessSwal(title, text);
      } catch (error) {
        if (this.form.payment_option === "INITIAL PAYMENT") {
          if ("messageKey" in (error.response?.data || {})) {
            let message = error.response.data.messageKey;

            message =
              this.t.errorMessages?.[message] || this.t.errorMessages.error;
            this.showErrorSwal(message, this.t.errorMessages.error);
            return;
          }

          let message = error.response?.data[0].message;

          if (
            message == "This transaction has been declined." &&
            this.selectedLanguage == "es"
          ) {
            message = "Esta transacción ha sido rechazada.";
          } else if (
            message == "This transaction has been declined." &&
            this.selectedLanguage == "en"
          ) {
            message = "This transaction has been declined.";
          }

          if (
            message == "The credit card number is invalid." &&
            this.selectedLanguage == "es"
          ) {
            message = "El número de tarjeta de crédito es inválido.";
          } else if (
            message == "The credit card number is invalid." &&
            this.selectedLanguage == "en"
          ) {
            message = "The credit card number is invalid.";
          }

          this.showErrorSwal(message, "Error");
        } else {
          const errorData = error.response?.data;
          const messageKey = errorData?.messageKey;
          const defaultMessage = this.t.errorMessages.error;

          const message = messageKey
            ? this.t.errorMessages?.[messageKey]
            : errorData?.error || defaultMessage;

          this.showErrorSwal(message, defaultMessage);
        }
      } finally {
        this.isProcessing = false;
      }
    },
    async checkIfIsValidLink() {
      try {
        this.isLoading = true;

        const { data } = await SecurePaymentService.isValidLink(
          this.uniqueCode
        );
        this.isValidLink = data.valid;

        if (!this.isValidLink) {
          this.isLoading = false;
        }
      } catch (error) {
        this.showErrorSwal(error, this.t.errorMessages.error);
      }
    },
    async getLinkData() {
      try {
        const { data } = await SecurePaymentService.getLinkData(
          this.uniqueCode
        );

        this.form.link_type = data.link_type;

        if (this.form.link_type === "LEAD") {
          this.form.first_program_id = data.first_program_id;
        } else {
          this.form.first_program_id = data.program;
        }

        this.form.name = data.name;
        this.form.first_name = data.first_name;
        this.form.last_name = data.last_name;
        this.form.currency = data.currency;
        this.form.type = data.type_action;
        this.form.program_id = data.program;
        this.form.language = data.language || "en";
        this.selectedLanguage = data.language || "en";
        this.form.concept = data.concept;
        this.form.payment_option = data.payment_option;

        if (
          this.form.payment_option === "INITIAL PAYMENT" ||
          this.form.payment_option === "MONTHLY PAYMENT" ||
          this.form.payment_option === "CHARGE"
        ) {
          this.form.payment_link_details = data.payment_link_details;
        }
        // else if (this.form.payment_option === "CHARGE") {
        //   let item = [
        //     {
        //       program_id: data.first_program_id,
        //       amount: data.amount,
        //       is_automatic_payment: false,
        //       program: data.concept,
        //     },
        //   ];
        //   this.form.payment_link_details = item;
        // }
        this.form.amount = data.amount;
        this.form.lead_id = data.lead_id; // nullable

        document.title = this.isRegisterCard
          ? this.t.registerCard
          : this.t.registerPayment;

        this.setFavIcon();
      } catch (error) {
        this.showErrorSwal(error, this.t.errorMessages.error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  created() {
    this.checkIfIsValidLink();
  },
  watch: {
    isValidLink(newValue) {
      if (newValue !== null && newValue === true) {
        this.getLinkData();
      }
    },
  },
};
</script>
<style lang="scss">
.pl-tooltip .tooltip-inner {
  max-width: fit-content !important;
  width: fit-content !important;
}
</style>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

.payment-link {
  font-family: "Montserrat", sans-serif !important;

  &-navbar {
    background-color: #fff;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  }

  .secure {
    font-weight: 500;
    font-size: clamp(1rem, 2.3vw, 1rem);
  }

  .transaction-list-mobile {
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: #f8f9fa;
    border-radius: 10px;
    // box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    .transaction-card {
      color: #fff;
      border-radius: 10px;
      margin: 0rem 0.5rem;
      background-color: #f0f0f0;

      .transaction-info {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .text-big {
          font-size: 1.1rem;
          font-weight: 600;
        }
      }

      .custom-border-top {
        border-top: 1px solid #ccc;
      }

      .transaction-details {
        margin-top: 10px;
        padding: 10px;
        background: #f0f0f0;
        border-radius: 8px;

        .message-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 4px;
        }
      }

      .text {
        font-size: 0.9rem;
        color: #333;
        font-weight: 500;
      }
    }
  }

  .transaction-list-desktop {
    width: 100%;
    padding: 1rem;

    .transaction-header-row,
    .transaction-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      background-color: #007bff;
      color: white;
      font-weight: bold;
      padding: 0.8rem;
      border-radius: 12px;
      margin-bottom: 0.5rem;
    }

    .transaction-row {
      background-color: #f1f3f5;
      color: #333;
      font-weight: normal;
      font-size: 1rem;

      .transaction-detail {
        background-color: #e9ecef;
        padding: 0.4rem;
        color: #555555f3;
        font-size: 0.9rem;
      }

      .transaction-value {
        padding: 0.4rem;
        text-align: left;

        .text-big {
          font-size: 1.1rem;
          font-weight: 600;
        }
      }
    }
  }

  .client-card {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background-color: #f2f9fe;
    border: 1px solid #0090e7;
    border-radius: 8px;
    padding: 0.75rem;
    width: 100%;
  }

  .client-card-2 {
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    width: 100%;
    /* Asegúrate de que ocupe el espacio disponible, pero no más */
  }

  .input-uppercase::placeholder {
    text-transform: none; /* Evita que el placeholder esté en mayúsculas */
  }

  .input-uppercase {
    text-transform: uppercase; /* Solo el contenido estará en mayúsculas */
  }

  .client-program {
    // width: clamp(30px, 10vw, 40px);
    // height: auto;
    // flex-shrink: 0;
    width: 35px !important;
    padding-bottom: 0.5rem;
  }

  .custom-label-class {
    // font-size: 1rem;
    font-size: clamp(0.75rem, 1.3vw, 1rem);
  }

  .mr-05 {
    margin-right: 0.5rem;
  }

  .mt-05 {
    margin-top: 0.5rem;
  }

  .pt-05 {
    padding-top: 0.5rem !important;
  }

  .ml-05 {
    margin-left: 0.5rem;
  }

  .mb-05 {
    margin-bottom: 0.5rem;
  }

  .pb-05 {
    padding-bottom: 0.5rem !important;
  }

  .px-05 {
    padding: 0.2rem 0.5rem !important;
  }

  .text {
    color: #323232;
    font-size: 1rem;
    font-weight: 600;
    // text-transform: capitalize;
  }
  .text-no-fw {
    color: #323232;
    font-size: 1rem;
    font-weight: 500;
    text-transform: capitalize;
  }

  // register card & pay
  &-pay {
    max-width: 95vw; // Tamaño por defecto (para pantallas muy pequeñas)
    margin: 0 auto;
    margin-top: 3rem;
    padding: 1.25rem;

    // Móviles pequeños (hasta 400px)
    @media (max-width: 400px) {
      max-width: 100vw;
      padding: 0rem;
    }

    // Móviles pequeños (hasta 575px)
    @media (max-width: 575.98px) {
      max-width: 95vw;
    }

    // Móviles grandes y tablets pequeñas (576px - 767px)
    @media (min-width: 576px) and (max-width: 767.98px) {
      max-width: 85vw;
    }

    // Tablets y pantallas medianas (768px - 1023px)
    @media (min-width: 768px) and (max-width: 1023.98px) {
      max-width: 70vw;
    }

    // Laptops y pantallas grandes (1024px - 1399px)
    @media (min-width: 1024px) and (max-width: 1399.98px) {
      max-width: 50vw;
    }

    // Pantallas extra grandes (1400px en adelante)
    @media (min-width: 1400px) {
      max-width: 35vw;
    }
  }

  &-pay-automatic {
    max-width: 95vw; // Tamaño por defecto (para pantallas muy pequeñas)
    margin: 0 auto;
    margin-top: 3rem;
    padding: 1.25rem;

    // Móviles pequeños (hasta 400px)
    @media (max-width: 400px) {
      max-width: 100vw;
      padding: 0rem;
      margin: 0;
    }

    // Móviles pequeños (hasta 575px)
    @media (max-width: 575.98px) {
      max-width: 95vw;
    }

    // Móviles grandes y tablets pequeñas (576px - 767px)
    @media (min-width: 576px) and (max-width: 767.98px) {
      max-width: 95vw;
    }

    // Tablets y pantallas medianas (768px - 1023px)
    @media (min-width: 768px) and (max-width: 1023.98px) {
      max-width: 70vw;
    }

    // Laptops y pantallas grandes (1024px - 1399px)
    @media (min-width: 1024px) and (max-width: 1399.98px) {
      max-width: 60vw;
    }

    // Pantallas extra grandes (1400px en adelante)
    @media (min-width: 1400px) {
      max-width: 40vw;
    }
  }

  // only register card
  &-card {
    max-width: 95vw; // Tamaño por defecto (para pantallas muy pequeñas)
    margin: 0 auto;
    margin-top: 3rem;
    padding: 1.25rem;

    // Móviles pequeños (hasta 400px)
    @media (max-width: 400px) {
      padding: 0rem;
      max-width: 100vw;
    }

    // Móviles pequeños (hasta 575px)
    @media (max-width: 575.98px) {
      max-width: 95vw;
    }

    // Móviles grandes y tablets pequeñas (576px - 767px)
    @media (min-width: 576px) and (max-width: 767.98px) {
      max-width: 80vw;
    }

    // Tablets y pantallas medianas (768px - 1023px)
    @media (min-width: 768px) and (max-width: 1023.98px) {
      max-width: 70vw;
    }

    // Laptops y pantallas grandes (1024px - 1399px)
    @media (min-width: 1024px) and (max-width: 1399.98px) {
      max-width: 50vw;
    }

    // Pantallas extra grandes (1400px en adelante)
    @media (min-width: 1400px) {
      max-width: 40vw;
    }
  }

  .position-relative {
    position: relative;
  }

  .card-icon-left {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 30px; // Ajusta según el tamaño deseado
    height: auto;
  }

  .card-icon-left-feather {
    position: absolute;
    left: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 1.5rem;
    height: auto;
  }

  .card-number-input {
    padding-left: 50px; // Espacio para la imagen
  }

  .credit-card-img {
    height: 20px;
    width: 50px;
    margin-right: 10px;
  }

  .language-select {
    width: auto;
    height: 35px;
    font-size: 1rem;
  }

  .payment-link-navbar .b-navbar-nav {
    display: flex;
    align-items: center;
  }

  .payment-link-navbar .b-nav-item {
    display: flex;
    align-items: center;
  }

  .payment-link-navbar .b-navbar-brand,
  .payment-link-navbar .b-navbar-toggle {
    display: flex;
    align-items: center;
  }

  .column-title {
    font-weight: 500;
    text-transform: capitalize;
    font-size: clamp(0.8rem, 1.3vw, 1rem);
    color: #323232;
  }

  .column-value {
    color: #323232;
    font-weight: 600;
    text-transform: capitalize;
    font-size: clamp(0.8rem, 1.3vw, 1rem);
  }

  .column-w-4 {
    width: 40% !important;
    min-width: 40% !important;
  }

  .column-w-2 {
    width: 20% !important;
    min-width: 20% !important;
  }

  .column-w-5 {
    width: 50% !important;
    min-width: 50% !important;
  }

  .payment-button {
    background-color: #47de8b !important;

    span {
      color: #ffffff;
      font-size: 1rem;
      font-weight: 600;
    }

    @media (max-width: 575.98px) {
      font-size: 1rem;
    }
  }

  .payment-detail {
    font-size: 1.2rem;
    font-weight: 600;

    @media (max-width: 575.98px) {
      font-size: 1rem;
    }
  }

  .payment-amount {
    font-size: 1.2rem;
    font-weight: 600;

    @media (max-width: 575.98px) {
      font-size: 1rem;
    }
  }

  // 1rem → Tamaño mínimo de la fuente (para pantallas pequeñas).
  // 3vw → Tamaño flexible basado en el ancho de la pantalla.
  // 1.8rem → Tamaño máximo de la fuente (para pantallas grandes).

  .type {
    color: #323232;
    font-size: clamp(1.3rem, 2.3vw, 1.8rem);
    font-weight: 700 !important;
  }

  .encrypted-message {
    font-size: 0.9rem;
    font-weight: 500;
    color: #6e6e6e;
  }

  .payment-detail-title {
    color: #323232;
    font-size: 1.2rem;
    font-weight: 700;
  }
}

.dark-layout {
  .payment-link {
    &-navbar {
      background-color: #17171a !important;
    }

    .invert {
      filter: invert(100%);
    }

    .type {
      color: #ffffff;
    }

    .encrypted-message {
      color: #6e6e6e;
    }

    .payment-detail-title {
      color: #ffffff;
    }

    .client-card {
      background-color: #17171a;
      border: 1px solid #0090e7;
    }

    .amg-text {
      color: #ffffff;
    }

    .column-title {
      color: #ffffff;
    }

    .column-value {
      color: #ffffff;
    }

    .text {
      color: #ffffff;
    }

    .text-no-fw {
      color: #ffffff;
    }

    .transaction-list-mobile {
      background-color: #17171a;

      .transaction-card {
        background-color: #1e1e21;

        .custom-border-top {
          border-top: 1px solid #333;
        }

        .transaction-details {
          background: #f0f0f0;
        }

        .text {
          color: #fff;
        }
      }
    }

    .transaction-list-desktop {
      .transaction-row {
        background-color: #1e1e21;
        color: #ffffff;

        .transaction-detail {
          background-color: #2a2a2e;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
